import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef, useMemo, type RefObject } from "react";
import { apiUrl, toAbsoluteAssetUrl } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import PhotoGrid from "@/components/images/PhotoGrid";
import PhotoViewer from "@/components/images/PhotoViewer";
import type { ImageFolderPhoto } from "@shared/api";
import { useIsMobile } from "@/hooks/use-mobile";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Eye, Download, MoreVertical, Trash2 } from "lucide-react";
import { notify } from "@/lib/notify";
import { uploadImages } from "@/lib/image-upload";

export function Activities({ id, headers }: { id: string; headers: any }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["deal-acts", id],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}/activities`), {
        headers,
      });
      const j = await r.json();
      return j.activities as any[];
    },
  });
  const add = useMutation({
    mutationFn: async (data: any) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}/activities`), {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(t("common.error"));
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-acts", id] }),
  });
  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const f = e.currentTarget as any;
          const type = f.type.value;
          const title = f.title.value;
          const due = f.due.value;
          add.mutate({ type, title, dueAt: due || null });
          f.reset();
        }}
        className="grid grid-cols-1 md:grid-cols-4 gap-2"
      >
        <select
          name="type"
          className="px-3 py-2 rounded-md border bg-background"
        >
          <option>{t('deal.activityTypeCall')}</option>
          <option>{t('deal.activityTypeEmail')}</option>
          <option>{t('deal.activityTypeMeeting')}</option>
          <option>{t('deal.activityTypeTask')}</option>
        </select>
        <input
          name="title"
          required
          className="md:col-span-2 px-3 py-2 rounded-md border bg-background"
          placeholder={t("deal.activityPlaceholder")}
        />
        <input
          name="due"
          type="datetime-local"
          className="px-3 py-2 rounded-md border bg-background"
        />
        <button className="px-3 py-2 rounded-md bg-primary text-primary-foreground">
          {t("deal.addActivity")}
        </button>
      </form>
      <ul className="divide-y rounded-md border">
        {(list.data || []).map((a: any) => (
          <li
            key={a.id}
            className="p-3 text-sm flex items-center justify-between"
          >
            <span>
              {a.type} — {a.title}
            </span>
            <span className="text-foreground/60">
              {a.dueAt ? new Date(a.dueAt).toLocaleString() : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ProductOption {
  id: string;
  label: string;
}

export function FilesTab({
  dealId,
  measurementProducts = [],
  fileInputRef,
}: {
  dealId: string;
  measurementProducts?: Array<{ id?: string; productType?: string; index?: number }>;
  /** Ref for the file input (Deal FAB uses this to trigger upload from quick actions). */
  fileInputRef?: RefObject<HTMLInputElement | null>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const productOptions: ProductOption[] = [
    { id: "", label: t("deal.filesDealGeneral") },
    ...(measurementProducts || [])
      .filter((p) => p.id)
      .map((p) => ({
        id: p.id!,
        label: `${p.productType ?? t("deal.product")} ${p.index ?? ""}`.trim(),
      })),
  ];
  const [uploadProductItemId, setUploadProductItemId] = useState<string>("");

  const [queue, setQueue] = useState<
    {
      id: string;
      file: File;
      status: "queued" | "uploading" | "done" | "error";
      progress: number;
    }[]
  >([]);
  const [busy, setBusy] = useState(false);

  const list = useQuery({
    queryKey: ["deal-files", dealId],
    queryFn: async () => {
      if (!dealId) return [];
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/files`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const j = await r.json();
      return j.files as any[];
    },
    enabled: !!dealId,
  });

  const del = useMutation({
    mutationFn: async (fileId: string) => {
      if (!dealId) throw new Error("Deal ID missing");
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/files`), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fileId }),
      });
      if (!r.ok) throw new Error(t("dealExtrasExtra.deleteError"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-files", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });

  const addToQueue = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const items = Array.from(files).map((f) => ({
      id: `${f.name}_${f.size}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      file: f,
      status: "queued" as const,
      progress: 0,
    }));
    setQueue((q) => [...q, ...items]);
  };

  const isImageFile = (file: File) => file.type.startsWith("image/");

  const uploadOneToDeal = (item: {
    id: string;
    file: File;
    status: any;
    progress: number;
  }): Promise<void> => {
    if (!dealId) {
      return Promise.reject(new Error("Deal ID missing"));
    }
    return new Promise((resolve) => {
      const fd = new FormData();
      fd.append("files", item.file);
      if (uploadProductItemId && uploadProductItemId.trim() !== "") {
        fd.append("product_item_id", uploadProductItemId.trim());
      }
      const xhr = new XMLHttpRequest();
      xhr.open("POST", apiUrl(`/api/crm/deals/${dealId}/files`));
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setQueue((q) =>
            q.map((it) =>
              it.id === item.id
                ? { ...it, progress: pct, status: "uploading" }
                : it,
            ),
          );
        }
      };
      xhr.onload = () => {
        setQueue((q) =>
          q.map((it) =>
            it.id === item.id
              ? {
                  ...it,
                  progress: 100,
                  status:
                    xhr.status >= 200 && xhr.status < 300 ? "done" : "error",
                }
              : it,
          ),
        );
        resolve();
      };
      xhr.onerror = () => {
        setQueue((q) =>
          q.map((it) => (it.id === item.id ? { ...it, status: "error" } : it)),
        );
        resolve();
      };
      xhr.send(fd);
    });
  };

  const startUpload = async () => {
    if (busy || queue.length === 0) return;
    setBusy(true);
    const queued = queue.filter((i) => i.status === "queued");
    try {
      const foldersRes = await fetch(apiUrl(`/api/images/folders?dealId=${dealId}`), { credentials: "include" });
      const foldersData = foldersRes.ok ? await foldersRes.json() : { folders: [] };
      const folders = foldersData.folders || [];
      const hasNoFolder = folders.length === 0;
      const imageItems = queued.filter((i) => isImageFile(i.file));
      const nonImageItems = queued.filter((i) => !isImageFile(i.file));

      let targetFolderId: string | null = hasNoFolder ? null : (folders[0]?.id ?? null);

      if (hasNoFolder && imageItems.length > 0) {
        let dealTitle = "Deal";
        try {
          const dealRes = await fetch(apiUrl(`/api/crm/deals/${dealId}`), { credentials: "include" });
          if (dealRes.ok) {
            const dealData = await dealRes.json();
            const d = dealData.deal ?? dealData;
            dealTitle = (d.title || d.name || "").trim() || "Deal";
          }
        } catch {
          /* use default */
        }
        const createRes = await fetch(apiUrl("/api/images/folders"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ name: dealTitle, dealId, category: "egyeb" }),
        });
        if (!createRes.ok) {
          const err = await createRes.json().catch(() => ({}));
          throw new Error(err?.userMessage || "Egyéb mappa létrehozása sikertelen.");
        }
        const { folder } = await createRes.json();
        targetFolderId = folder?.id ?? null;
        if (!targetFolderId) throw new Error("Egyéb mappa létrehozása sikertelen.");
      }

      if (targetFolderId && imageItems.length > 0) {
        const fyId = targetFolderId;
        const remaining = new Set(imageItems.map((i) => i.id));
        await uploadImages({
          uploadUrl: apiUrl(`/api/images/folders/${fyId}/photos`),
          files: imageItems.map((i) => i.file),
          extraFields: { subfolderType: "munkaink" },
          context: { folderId: fyId, dealId },
          onEachSuccess: () => {
            // Mark the next still-pending image item as done (per-file order is preserved).
            const nextId = remaining.values().next().value as string | undefined;
            if (nextId) {
              remaining.delete(nextId);
              setQueue((q) =>
                q.map((it) =>
                  it.id === nextId ? { ...it, progress: 100, status: "done" } : it,
                ),
              );
            }
          },
        });
        // Anything not confirmed was queued to the outbox (will sync) — show as done.
        setQueue((q) =>
          q.map((it) =>
            remaining.has(it.id) ? { ...it, progress: 100, status: "done" } : it,
          ),
        );
        qc.invalidateQueries({ queryKey: ["image-folders"] });
        qc.invalidateQueries({ queryKey: ["image-folders", { dealId }] });
      }

      for (const item of nonImageItems) {
        await uploadOneToDeal(item);
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Feltöltés hiba.");
    }
    setBusy(false);
    qc.invalidateQueries({ queryKey: ["deal-files", dealId] });
  };

  const files = (list.data || []).filter((f: any) =>
    !(f.mime || "").startsWith("image/"),
  );

  const formatFileSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const fileTypeLabel = (f: any) => {
    if (f.kind === "configurator_quote") return "Cenová ponuka";
    const mime = (f.mime || "").toLowerCase();
    if (mime.includes("pdf")) return "PDF";
    if (mime.includes("sheet") || mime.includes("excel")) return "Táblázat";
    if (mime.includes("word") || mime.includes("document")) return "Dokument";
    return f.kind || mime.split("/")[1] || "–";
  };

  return (
    <div className="space-y-4">
      <div>
        {productOptions.length > 1 && (
          <div className="mb-2">
            <label className="block text-sm text-foreground/70 mb-1">{t("dealExtrasExtra.uploadLocation")}</label>
            <select
              value={uploadProductItemId}
              onChange={(e) => setUploadProductItemId(e.target.value)}
              className="px-3 py-2 rounded-md border bg-background text-sm"
            >
              {productOptions.map((opt) => (
                <option key={opt.id || "deal"} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="block text-sm text-foreground/70">
          {t("dealExtrasExtra.dragFilesOrChoose")}
        </label>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            addToQueue(e.dataTransfer.files);
          }}
          className="border-2 border-dashed rounded-md p-6 text-center text-sm text-foreground/60"
        >
          {t("dealExtrasExtra.dragDrop")}
          <div className="mt-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => addToQueue(e.target.files)}
            />
          </div>
        </div>
        {queue.length > 0 && (
          <div className="mt-3 space-y-2">
            {queue.map((it) => (
              <div
                key={it.id}
                className="p-2 border rounded-md flex items-center justify-between"
              >
                <div className="truncate pr-2 text-sm">{it.file.name}</div>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${it.status === "done" ? "bg-green-600 text-white" : it.status === "error" ? "bg-red-600 text-white" : "bg-muted"}`}
                  >
                    {it.status === "uploading"
                      ? `${it.progress}%`
                      : it.status === "done"
                        ? "✓"
                        : it.status === "error"
                          ? "!"
                          : "•"}
                  </div>
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <button
                onClick={startUpload}
                disabled={busy}
                className="px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
              >
                {t("dealExtrasExtra.upload")}
              </button>
            </div>
          </div>
        )}
      </div>

      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">{t("dealExtrasExtra.noFilesMessage")}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">{t("manualsExtra.fileNameHead")}</TableHead>
                <TableHead className="w-[120px]">{t("dealExtrasExtra.typeHead")}</TableHead>
                <TableHead className="w-[90px]">{t("manualsExtra.sizeHead")}</TableHead>
                <TableHead className="w-[160px] text-right">{t("manualsExtra.actionsHead")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((f: any) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                      <a
                        className="text-primary truncate max-w-[280px] hover:underline"
                        href={toAbsoluteAssetUrl(f.url)}
                        target="_blank"
                        rel="noreferrer"
                        title={f.name}
                      >
                        {f.name}
                      </a>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {fileTypeLabel(f)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatFileSize(f.size)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0"
                        onClick={() => window.open(toAbsoluteAssetUrl(f.url), "_blank")}
                        title={t("manuals.view")}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          const url = toAbsoluteAssetUrl(f.url);
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = f.name || "download";
                          link.target = "_blank";
                          link.click();
                        }}
                        title={t("manuals.download")}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0"
                            title={t("dealExtrasExtra.moreActions")}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              if (confirm(t("confirm.deleteFile"))) del.mutate(f.id);
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("dealExtrasExtra.deleteLabel")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function PhotosTab({ dealId }: { dealId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  /** Which category the next file-input pick should go to (set before opening the picker). */
  const uploadSubfolderTypeRef = useRef<"bemeres" | "munkaink" | "marketing">("munkaink");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch image folders for this deal (single source of truth for photos)
  const imageFoldersQuery = useQuery({
    queryKey: ["image-folders", { dealId }],
    queryFn: async () => {
      if (!dealId) return [];
      const r = await fetch(apiUrl(`/api/images/folders?dealId=${dealId}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const j = await r.json();
      return j.folders as any[];
    },
    enabled: !!dealId,
  });

  // Fetch photos for each folder
  const folderPhotosQueries = useQuery({
    queryKey: ["image-folders-photos", dealId, imageFoldersQuery.data?.map(f => f.id)],
    queryFn: async () => {
      if (!imageFoldersQuery.data || imageFoldersQuery.data.length === 0) return [];
      
      // Fetch all folders' photos in parallel
      const folderPromises = imageFoldersQuery.data.map(async (folder: any) => {
        try {
          const r = await fetch(apiUrl(`/api/images/folders/${folder.id}`), {
            credentials: "include",
          });
          if (!r.ok) return null;
          const j = await r.json();
          return { folderId: folder.id, folderName: folder.name, folder: j.folder };
        } catch (error) {
          console.error(`Error fetching folder ${folder.id}:`, error);
          return null;
        }
      });

      const folderResults = await Promise.all(folderPromises);
      return folderResults.filter(Boolean);
    },
    enabled: !!dealId && !!imageFoldersQuery.data && imageFoldersQuery.data.length > 0,
  });

  // Collect all photos from image folders (single source of truth)
  const folderPhotosList: (ImageFolderPhoto & { _folderId?: string; _folderName?: string; _source?: "image-folder" })[] = (folderPhotosQueries.data || []).flatMap((folderData: any) => {
    if (!folderData || !folderData.folder || !folderData.folder.photos) return [];
    
    const allPhotos: ImageFolderPhoto[] = [
      ...(folderData.folder.photos.bemeres || []),
      ...(folderData.folder.photos.munkaink || []),
      ...(folderData.folder.photos.marketing || []),
    ];
    
    // Add folder metadata to photos for identification
    return allPhotos.map((photo: ImageFolderPhoto) => ({
      ...photo,
      _folderId: folderData.folderId,
      _folderName: folderData.folderName,
      _source: "image-folder" as const,
    }));
  });

  const allPhotos = folderPhotosList;
  const folderId = imageFoldersQuery.data?.[0]?.id as string | undefined;

  // Group folder photos by subfolder type
  const folderPhotosByType = {
    bemeres: folderPhotosList.filter(p => p.subfolder_type === "bemeres"),
    munkaink: folderPhotosList.filter(p => p.subfolder_type === "munkaink"),
    marketing: folderPhotosList.filter(p => p.subfolder_type === "marketing"),
  };

  // Bemérésre: flat list with auto labels "Garážová brána 1-1", "Garážová brána 1-2", ...
  const bemeresPhotosWithLabels = (() => {
    const bemeres = folderPhotosByType.bemeres;
    type PhotoWithSlug = (typeof folderPhotosList)[number] & { product_slug?: string | null };
    const sorted = [...bemeres].sort((a, b) => {
      const sa = ((a as PhotoWithSlug).product_slug ?? "").toString();
      const sb = ((b as PhotoWithSlug).product_slug ?? "").toString();
      if (sa !== sb) return sa === "" ? -1 : sb === "" ? 1 : sa.localeCompare(sb);
      return (a.order_index ?? 0) - (b.order_index ?? 0);
    });
    let lastSlug: string | undefined;
    let indexInProduct = 0;
    return sorted.map((p) => {
      const slug = ((p as PhotoWithSlug).product_slug ?? "").toString();
      if (slug !== lastSlug) {
        lastSlug = slug;
        indexInProduct = 1;
      } else {
        indexInProduct++;
      }
      const prefix = slug || t("imagesFolderExtra.bemeresUnspecifiedLabel");
      const _displayLabel = `${prefix}-${indexInProduct}`;
      return { ...p, _displayLabel } as typeof p & { _displayLabel: string };
    });
  })();

  const [viewerPhoto, setViewerPhoto] = useState<{ photo: ImageFolderPhoto & { _folderId?: string; _folderName?: string }; index: number; allPhotos: (ImageFolderPhoto & { _folderId?: string; _folderName?: string })[] } | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [selectionModeEnabled] = useState(false);
  const selectionMode = selectionModeEnabled && selectedPhotos.size > 0;
  const [uploadBusy, setUploadBusy] = useState(false);

  const photoLabelById = useMemo(() => {
    const m = new Map<string, string>();
    bemeresPhotosWithLabels.forEach((p) => m.set(p.id, (p as typeof p & { _displayLabel: string })._displayLabel));
    return m;
  }, [bemeresPhotosWithLabels]);

  // Ensure folder exists; create if none. Returns folder id.
  const ensureFolder = async (): Promise<string> => {
    if (folderId) return folderId;
    let dealTitle = "Deal";
    try {
      const dealRes = await fetch(apiUrl(`/api/crm/deals/${dealId}`), { credentials: "include" });
      if (dealRes.ok) {
        const dealData = await dealRes.json();
        const d = dealData.deal ?? dealData;
        dealTitle = (d.title || d.name || "").trim() || "Deal";
      }
    } catch {
      /* use default */
    }
    const createRes = await fetch(apiUrl("/api/images/folders"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name: dealTitle, dealId, category: "egyeb" }),
    });
    if (!createRes.ok) {
      const err = await createRes.json().catch(() => ({}));
      throw new Error(err?.userMessage || "Mappa létrehozása sikertelen.");
    }
    const { folder } = await createRes.json();
    if (!folder?.id) throw new Error("Mappa létrehozása sikertelen.");
    qc.invalidateQueries({ queryKey: ["image-folders", { dealId }] });
    qc.invalidateQueries({ queryKey: ["image-folders-photos", dealId] });
    return folder.id;
  };

  const uploadToFolder = async (files: File[], subfolderType: "bemeres" | "munkaink" | "marketing") => {
    if (!files.length || !dealId) return;
    setUploadBusy(true);
    try {
      const targetFolderId = await ensureFolder();
      const res = await uploadImages({
        uploadUrl: apiUrl(`/api/images/folders/${targetFolderId}/photos`),
        files,
        extraFields: { subfolderType },
        context: { folderId: targetFolderId, dealId },
      });
      if (res.uploaded > 0) {
        qc.invalidateQueries({ queryKey: ["image-folders", { dealId }] });
        qc.invalidateQueries({ queryKey: ["image-folders-photos", dealId] });
      }
      if (res.queued > 0) {
        notify.info(
          t("uploadOutbox.queuedDealPhotos", { count: res.queued }),
          t("uploadOutbox.queuedDealPhotosDesc"),
        );
      }
    } catch (e) {
      notify.error(e instanceof Error ? e.message : "Feltöltés hiba.");
    } finally {
      setUploadBusy(false);
    }
  };

  const deleteFolderPhoto = useMutation({
    mutationFn: async ({ folderId: fid, photoId }: { folderId: string; photoId: string }) => {
      const r = await fetch(apiUrl(`/api/images/folders/${fid}/photos/${photoId}`), {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("dealExtrasExtra.deleteFailed"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folders", { dealId }] });
      qc.invalidateQueries({ queryKey: ["image-folders-photos", dealId] });
    },
  });

  const handlePhotoClick = (photo: ImageFolderPhoto, index: number, allPhotos: ImageFolderPhoto[]) => {
    setViewerPhoto({ photo, index, allPhotos });
  };

  const handleViewerNavigate = (newIndex: number) => {
    if (viewerPhoto) {
      setViewerPhoto({
        ...viewerPhoto,
        photo: viewerPhoto.allPhotos[newIndex],
        index: newIndex,
      });
    }
  };

  const handleUploadClick = (subfolderType: "bemeres" | "munkaink" | "marketing") => {
    uploadSubfolderTypeRef.current = subfolderType;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Copy FileList before clearing the input — browsers empty the live FileList when value="".
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (files.length > 0) {
      void uploadToFolder(files, uploadSubfolderTypeRef.current);
    }
  };

  return (
    <div className="space-y-6">
      <input
        type="file"
        accept="image/*"
        multiple
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Bemérésre: flat grid with labels under each photo (e.g. Garážová brána 1-1, 1-2, …) */}
      <PhotoGrid
        photos={bemeresPhotosWithLabels}
        subfolderType="bemeres"
        sectionTitle={t("imagesFolderExtra.subfolderBemeres")}
        selectedPhotos={selectedPhotos}
        onSelectPhoto={(id, checked) => {
          const next = new Set(selectedPhotos);
          if (checked) next.add(id);
          else next.delete(id);
          setSelectedPhotos(next);
        }}
        selectionMode={selectionMode}
        selectionModeEnabled={selectionModeEnabled}
        onPhotoClick={(photo, index) => {
          const globalIndex = allPhotos.findIndex(p => p.id === photo.id);
          handlePhotoClick(photo, globalIndex >= 0 ? globalIndex : 0, allPhotos);
        }}
        onUploadClick={() => handleUploadClick("bemeres")}
        onDropFiles={(files) => void uploadToFolder(Array.from(files), "bemeres")}
        onDeletePhoto={(photo) => {
          const fid = (photo as ImageFolderPhoto & { _folderId?: string })._folderId;
          if (fid && confirm(t("confirm.deletePhoto"))) {
            deleteFolderPhoto.mutate({ folderId: fid, photoId: photo.id });
          }
        }}
        getPhotoLabel={(p) => (p as ImageFolderPhoto & { _displayLabel?: string })._displayLabel ?? ""}
      />
      <PhotoGrid
        photos={folderPhotosByType.munkaink}
        subfolderType="munkaink"
        selectedPhotos={selectedPhotos}
        onSelectPhoto={(id, checked) => {
          const next = new Set(selectedPhotos);
          if (checked) next.add(id);
          else next.delete(id);
          setSelectedPhotos(next);
        }}
        selectionMode={selectionMode}
        selectionModeEnabled={selectionModeEnabled}
        onPhotoClick={(photo, index) => {
          const allBemeres = folderPhotosByType.bemeres;
          const allMunkaink = folderPhotosByType.munkaink;
          const globalIndex = allBemeres.length + allMunkaink.findIndex(p => p.id === photo.id);
          handlePhotoClick(photo, globalIndex >= 0 ? globalIndex : 0, allPhotos);
        }}
        onUploadClick={() => handleUploadClick("munkaink")}
        onDropFiles={(files) => void uploadToFolder(Array.from(files), "munkaink")}
        onDeletePhoto={(photo) => {
          const fid = (photo as ImageFolderPhoto & { _folderId?: string })._folderId;
          if (fid && confirm(t("confirm.deletePhoto"))) {
            deleteFolderPhoto.mutate({ folderId: fid, photoId: photo.id });
          }
        }}
      />
      <PhotoGrid
        photos={folderPhotosByType.marketing}
        subfolderType="marketing"
        selectedPhotos={selectedPhotos}
        onSelectPhoto={(id, checked) => {
          const next = new Set(selectedPhotos);
          if (checked) next.add(id);
          else next.delete(id);
          setSelectedPhotos(next);
        }}
        selectionMode={selectionMode}
        selectionModeEnabled={selectionModeEnabled}
        onPhotoClick={(photo, index) => {
          const allBemeres = folderPhotosByType.bemeres;
          const allMunkaink = folderPhotosByType.munkaink;
          const allMarketing = folderPhotosByType.marketing;
          const globalIndex = allBemeres.length + allMunkaink.length + allMarketing.findIndex(p => p.id === photo.id);
          handlePhotoClick(photo, globalIndex >= 0 ? globalIndex : 0, allPhotos);
        }}
        onUploadClick={() => handleUploadClick("marketing")}
        onDropFiles={(files) => void uploadToFolder(Array.from(files), "marketing")}
        onDeletePhoto={(photo) => {
          const fid = (photo as ImageFolderPhoto & { _folderId?: string })._folderId;
          if (fid && confirm(t("confirm.deletePhoto"))) {
            deleteFolderPhoto.mutate({ folderId: fid, photoId: photo.id });
          }
        }}
      />

      {allPhotos.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          {t("dealExtrasExtra.noPhotosMessage")}
        </p>
      )}

      {/* Photo Viewer */}
      {viewerPhoto && (
        <PhotoViewer
          photos={viewerPhoto.allPhotos}
          currentIndex={viewerPhoto.index}
          onClose={() => setViewerPhoto(null)}
          onNavigate={handleViewerNavigate}
          getPhotoLabel={(p) => photoLabelById.get(p.id) ?? ""}
        />
      )}
    </div>
  );
}

export function StageHistory({ deal }: { deal: any }) {
  const items = (deal.history || [])
    .filter((h: any) => h.type === "stage_changed")
    .slice()
    .reverse();
  return (
    <div className="relative pl-6">
      <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
      <ul className="space-y-4 text-sm">
        {items.map((h: any) => (
          <li key={h.id} className="relative">
            <div className="absolute -left-[6px] top-1 h-3 w-3 rounded-full bg-primary" />
            <div className="p-3 border rounded-md bg-card">
              <div className="font-medium capitalize">{h.type}</div>
              <div className="text-foreground/60">
                {new Date(h.at).toLocaleString()}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DealTimeline({ deal }: { deal: any }) {
  const { t } = useTranslation();
  const items = (deal.history || [])
    .slice()
    .sort((a: any, b: any) => b.at.localeCompare(a.at));

  const detailText = (h: any) => {
    if (h.type === "created") return t("timeline.created");
    if (h.type === "stage_changed") {
      const from = h.detail?.fromStage;
      const to = h.detail?.toStage;
      if (from && to) return `${t("timeline.stageChanged")}: '${from}' → '${to}'`;
      return t("timeline.stageChanged");
    }
    if (h.type === "deal_updated" || h.type === "updated") {
      const changed = h.detail?.changed;
      if (Array.isArray(changed) && changed.length > 0) {
        const fields = changed.map((c: any) => c.field).join(", ");
        return `${t("timeline.dealUpdated")}: ${fields}`;
      }
      return t("timeline.dealUpdated");
    }
    if (h.type === "note_added") return t("timeline.noteAdded");
    if (h.type === "note_updated") return t("timeline.noteUpdated");
    if (h.type === "note_deleted") return t("timeline.noteDeleted");
    if (h.type === "email_sent")
      return `${t("timeline.emailSent")} (${h.detail?.title || ""})`;
    if (h.type === "uploaded_file")
      return `${t("timeline.fileUploaded")}: ${h.detail?.name || ""}`;
    if (h.type === "uploaded_photo")
      return `${t("timeline.photoUploaded")}: ${h.detail?.name || ""}`;
    if (h.type === "deleted_file")
      return `${t("timeline.fileDeleted")}: ${h.detail?.name || ""}`;
    if (h.type === "deleted_photo")
      return `${t("timeline.photoDeleted")}: ${h.detail?.name || ""}`;
    if (h.type === "contract_generated")
      return `${t("timeline.contractGenerated")}: ${h.detail?.templateName || ""}`;
    if (h.type === "contract_deleted")
      return `${t("timeline.contractDeleted")}: ${h.detail?.templateName || ""}`;
    if (h.type === "contract_regenerated")
      return `${t("timeline.contractRegenerated")}: ${h.detail?.templateName || ""}`;
    if (h.type === "document_generated")
      return `${t("timeline.documentGenerated")}: ${h.detail?.documentType || ""}${h.detail?.documentNumber ? ` (${h.detail.documentNumber})` : ""}`;
    if (h.type === "documents_synced") {
      const s = h.detail?.synced ?? 0;
      const b = h.detail?.backfilled ?? 0;
      const parts = [];
      if (s > 0) parts.push(t("timeline.documentsSyncedCount", { count: s }));
      if (b > 0) parts.push(t("timeline.documentsBackfilledCount", { count: b }));
      return t("timeline.documentsSynced") + (parts.length ? `: ${parts.join(", ")}` : "");
    }
    return h.type;
  };

  const iconClass = (h: any) => {
    if (h.type === "stage_changed") return "bg-blue-600";
    if (h.type?.startsWith("note_")) return "bg-amber-500";
    if (h.type?.startsWith("uploaded_")) return "bg-green-600";
    if (h.type?.startsWith("deleted_")) return "bg-red-600";
    if (h.type === "email_sent") return "bg-purple-600";
    if (h.type === "deal_updated" || h.type === "updated") return "bg-indigo-600";
    if (h.type === "created") return "bg-emerald-600";
    if (h.type === "contract_generated" || h.type === "contract_regenerated") return "bg-violet-600";
    if (h.type === "contract_deleted") return "bg-red-600";
    if (h.type === "document_generated") return "bg-teal-600";
    if (h.type === "documents_synced") return "bg-cyan-600";
    return "bg-primary";
  };

  // Group events by date (YYYY-MM-DD)
  const grouped = new Map<string, typeof items>();
  for (const h of items) {
    const d = new Date(h.at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(h);
  }

  if (items.length === 0) {
    return <div className="text-center py-8 text-muted-foreground text-sm">{t("timeline.noEvents")}</div>;
  }

  return (
    <div className="relative pl-4">
      <div className="absolute left-[7px] top-3 bottom-3 w-0.5 bg-border" />
      <div className="space-y-6">
        {[...grouped.entries()].map(([dateKey, events]) => {
          const d = new Date(dateKey + "T00:00:00");
          const dateLabel = d.toLocaleDateString("hu-HU", { year: "numeric", month: "short", day: "numeric" });
          return (
            <div key={dateKey}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 relative z-10" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{dateLabel}</span>
              </div>
              <div className="space-y-2 ml-6">
                {events.map((h: any) => {
                  const time = new Date(h.at).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" });
                  return (
                    <div key={h.id} className="relative flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className={`h-2 w-2 rounded-full ${iconClass(h)}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">{time}</span>
                          <span className="text-sm font-medium text-foreground">{h.by}</span>
                        </div>
                        <p className="text-sm text-foreground/80 mt-0.5">{detailText(h)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
